import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Spin,
  Result,
  Button,
  message
} from 'antd';
import { correctionAPI, essayAPI } from '../services/api';
import { Essay, Correction, ErrorRecord, DimensionScores } from '../types';
import CorrectionResultDisplay from '../components/CorrectionResultDisplay';
import { generateCorrectionDOCX } from '../services/docxExport';
import './CorrectionResult.css';
import '../styles/correction-common.css';

interface CorrectionData {
  correction: Correction;
  errors: ErrorRecord[];
  essay: Essay;
  dimension_scores?: DimensionScores;
  workflow?: any; // 添加workflow属性
}

const CorrectionResult: React.FC = () => {
  const { essayId } = useParams<{ essayId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CorrectionData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCorrectionData();
    
    // 如果正在批改中，定时刷新状态
    const interval = setInterval(() => {
      if (data?.essay?.status === 'correcting' || data?.essay?.status === 'processing') {
        fetchCorrectionData();
      }
    }, 3000); // 每3秒刷新一次
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [essayId, data?.essay?.status]);

  const fetchCorrectionData = async () => {
    if (!essayId) return;
    
    setLoading(true);
    try {
      const response = await correctionAPI.get(parseInt(essayId), { skipErrorHandler: true });
      setData(response.data);
      setError(null);
    } catch (err: any) {
      // 当前轮次还未完成时，回退到作文工作流状态页。
      if (err.response?.status === 404 || err.response?.status === 409) {
        try {
          const essayResponse = await essayAPI.get(parseInt(essayId));
          const { essay, workflow } = essayResponse.data;
          
          // 根据作文状态显示不同的信息，而不是设置错误
          setData({ 
            essay, 
            correction: null as any, 
            errors: [],
            workflow 
          } as any);
          
          // 只有真正的错误才设置错误状态
          if (essay.status === 'failed') {
            setError('处理失败');
          } else {
            setError(null); // 清除错误，显示状态信息
          }
        } catch {
          setError('作文不存在');
        }
      } else {
        setError(err.response?.data?.error || '获取批改结果失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!data) {
      message.error('没有可导出的数据');
      return;
    }

    try {
      const loadingMessage = message.loading('正在生成Word文档...', 0);

      // 判断是否有范文数据，决定生成类型
      const hasModelEssay = data.correction &&
        data.correction.corrected_text &&
        data.correction.major_changes;

      // 构造导出数据，包含corrected_version
      const exportData = {
        correction: data.correction,
        errors: data.errors || [],
        essay: data.essay,
        dimension_scores: data.dimension_scores,
        corrected_version: hasModelEssay ? {
          full_text: data.correction.corrected_text,
          major_changes: JSON.parse(data.correction.major_changes || '[]')
        } : undefined
      };

      // 生成DOCX文档
      await generateCorrectionDOCX(exportData);

      loadingMessage();
      message.success('Word文档导出成功！');
    } catch (err) {
      console.error('Word文档导出失败:', err);
      message.error('Word文档导出失败，请重试');
    }
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" tip="加载批改结果中..." />
      </div>
    );
  }

  // 如果有作文数据但没有批改结果，显示进度状态或继续流程
  if (data?.essay && !data.correction) {
    const { essay, workflow } = data;

    // 根据作文实际状态决定操作
    if (!essay.original_file) {
      // 没有上传文件，提示上传
      return (
        <Result
          status="warning"
          title="请先上传作文文件"
          subTitle="该作文尚未上传文件，请先上传作文文件后再进行批改"
          extra={[
            <Button
              type="primary"
              onClick={() => navigate(`/essay-wizard/continue/${essayId}?step=1`)}
              key="upload"
            >
              前往上传
            </Button>,
            <Button onClick={() => navigate('/history')} key="back">
              返回列表
            </Button>
          ]}
        />
      );
    } else if (essay.corrected_text || essay.original_text) {
      // 有文本内容（已识别），直接跳转到批改步骤
      window.location.href = `/essay-wizard/continue/${essayId}?step=3`;
      return (
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size="large" tip="正在跳转到批改页面..." />
        </div>
      );
    }

    // 否则显示当前状态
    const statusConfig = {
      pending: { status: 'info' as const, title: '等待处理', subTitle: '作文已创建，等待处理' },
      processing: { status: 'info' as const, title: '处理中', subTitle: '正在识别作文内容...' },
      correcting: { status: 'info' as const, title: '批改中', subTitle: 'AI正在分析和批改您的作文，请稍候...' },
      completed: { status: 'warning' as const, title: '等待查看', subTitle: '批改已完成，正在生成报告...' },
      failed: { status: 'error' as const, title: '处理失败', subTitle: '作文处理过程中出现错误' }
    };

    const config = statusConfig[essay.status as keyof typeof statusConfig] ||
                   { status: 'info' as const, title: '处理中', subTitle: '请稍候...' };

    // 获取正确的步骤
    const currentStep = workflow?.current_step || 2; // 默认到上传步骤
    
    return (
      <Result
        status={config.status}
        title={config.title}
        subTitle={config.subTitle}
        extra={[
          essay.status === 'failed' ? (
            <Button 
              type="primary" 
              onClick={() => navigate(`/essay-wizard/continue/${essayId}`)}
              key="retry"
            >
              重新处理
            </Button>
          ) : essay.status === 'correcting' ? (
            <Button type="primary" loading key="waiting">
              批改中...
            </Button>
          ) : (
            <Button 
              type="primary" 
              onClick={() => navigate(`/essay-wizard/continue/${essayId}?step=${currentStep}`)}
              key="continue"
            >
              继续操作
            </Button>
          ),
          <Button onClick={() => navigate('/history')} key="back">
            返回列表
          </Button>
        ]}
      />
    );
  }

  if (error) {
    return (
      <Result
        status="error"
        title="加载失败"
        subTitle={error}
        extra={[
          <Button type="primary" onClick={() => navigate('/wizard')} key="retry">
            重新批改
          </Button>,
          <Button onClick={() => navigate('/history')} key="back">
            返回列表
          </Button>
        ]}
      />
    );
  }

  if (!data) {
    return <Result status="404" title="404" subTitle="批改结果不存在" />;
  }

  // 使用CorrectionResultDisplay组件展示
  return (
    <div className="correction-result-page">
      <div className="content-wrapper">
        <CorrectionResultDisplay
          correctionData={data}
          showFullDetails={true}
          showScoreCard={true}
          showErrorList={true}
          showTabs={true}
          showActions={true}
          showComments={true}
          tabs={{
            showOriginal: true,
            showErrors: true,
            showStats: true
          }}
          onExport={handleExport}
          onNavigate={handleNavigate}
        />
        
        {/* 页面级操作按钮 */}
        <div className="action-buttons" style={{ 
          marginTop: 24, 
          textAlign: 'center',
          padding: '20px',
          background: 'white',
          borderRadius: '8px'
        }}>
          <Button 
            type="primary" 
            size="large" 
            onClick={handleExport}
            style={{ marginRight: 16 }}
          >
            导出Word
          </Button>
          <Button 
            size="large" 
            onClick={() => navigate('/history')}
          >
            返回列表
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CorrectionResult;
